import Vue from "vue";
import isEmpty from "lodash.isempty";
import hasIn from "lodash.hasin";
import { getField, updateField } from "vuex-map-fields";

import { $http } from "@/utils";

export default {
  namespaced: true,
  state: {
    registerUsername: "",
    registerEmail: "",
    registerPassword: "",
    password: "",
    remember_me: true,
    is_register: false,
    is_copy_to_register: false,
    registration: {
      business_name: "",
      first_name: "",
      last_name: "",
      lab_id: null,
      error: false
    },
    copyToRegistration: {
      error: false,
      data: {
        username: "",
        email: "",
        first_name: "",
        last_name: "",
        business_name: "",
        password: "",
        uuid: ""
      }
    },
    user: {
      id: null,
      first_name: "",
      last_name: "",
      email: "",
      client_id: "",
      lab_id: ""
    },
    activeTab: null,
    error: false,
    message: false
  },
  getters: {
    getField,
    user() {
      return Vue.auth.user();
    },
    clientIds(state) {
      return state.user.clients.map(a => a.id);
    }
  },
  mutations: {
    updateField,
    SET_MESSAGE(state, val) {
      state.message = val ? val : false;
    },
    SET_ERROR(state, val) {
      state.error = val ? val : false;
    },
    SET_ERROR_REGISTRATION(state, val = undefined) {
      if (val === undefined) {
        // Default error message
        val =
          "There was an issue registering your account. <br />Please contact CVAS Admin Support for help";
      }

      state.registration.error = val;
    },
    CLEAR_PASSWORD(state) {
      state.password = "";
    },
    SET_USER_DATA(state, data) {
      state.user = data;
    },
    SET_USER_DATA_COPY_TO(state, data) {
      Vue.set(state.copyToRegistration, "data", data);
    },
    SET_ERROR_COPY_TO(state, val = undefined) {
      if (val === undefined) {
        // Default error message
        val = "This account is already registered";
      }

      state.copyToRegistration.error = val;
    },
    clearRegistrationDetails(state) {
      Vue.set(state, "registration", {
        business_name: "",
        first_name: "",
        last_name: "",
        lab_id: null,
        error: false
      });
      Vue.set(state, "registerUsername", "");
      Vue.set(state, "registerEmail", "");
      Vue.set(state, "registerPassword", "");
    }
  },
  actions: {
    fetch(data) {
      return Vue.auth.fetch(data);
    },
    refresh(data) {
      return Vue.auth.refresh(data);
    },
    getUser({ commit }) {
      return Vue.axios.get("/users/me").then(({ data }) => {
        commit("SET_USER_DATA", data?.data);
        return data.success || false;
      });
    },
    login({ state, commit, getters }, data = {}) {
      commit("SET_ERROR"); // Clear error before logging in
      commit("SET_MESSAGE"); // Clear messages
      console.log(state);
      return new Promise(resolve => {
        Vue.auth
          .login({
            data: {
              username: state.is_register
                ? state.registerUsername
                : state.is_copy_to_register
                ? state.copyToRegistration?.data?.username
                : state.username,
              password: state.is_register
                ? state.registerPassword
                : state.is_copy_to_register
                ? state.copyToRegistration?.data?.password
                : state.password
            },
            remember: state.remember_me,
            staySignedIn: state.remember_me
          })
          .then(res => {
            commit("CLEAR_PASSWORD");
            if (data.remember) {
              Vue.auth.remember(
                JSON.stringify({
                  name: getters.user.first_name
                })
              );
            }
            console.log("Logged In!");
            resolve(res);
          })
          .catch(e => {
            console.error("login error", e);
            commit("CLEAR_PASSWORD");

            // Check for saved error message in localStorage
            const storedErrorMessage = localStorage.getItem(
              "usersFetchErrorMessage"
            );
            if (storedErrorMessage) {
              commit("SET_ERROR", storedErrorMessage);
              // Clear the stored message after use
              localStorage.removeItem("usersFetchErrorMessage");
              return;
            }

            // Fall back to existing error handling
            if (e.response && hasIn(e.response, "data.message")) {
              commit("SET_ERROR", e.response.data.message);
              return;
            }

            commit("SET_ERROR", "Incorrect username or password");
          });
      });
    },
    logout({ dispatch }) {
      dispatch("clearState", null, { root: true });
      return Vue.auth.logout();
    },
    requestPasswordReset(ctx, email) {
      return $http
        .post(`/users/password-reset`, { email })
        .then(({ data }) => (data.success ? true : data))
        .catch(e => {
          if (e.response && hasIn(e.response, "data")) return e.response.data;
          return {
            success: false,
            data: { message: "Network Error - Please try again" }
          };
        });
    },
    passwordReset(ctx, { token, password, password_confirm }) {
      return $http
        .post(`/users/password-reset/${token}`, {
          password,
          password_confirm
        })
        .then(({ data }) => (data.success ? true : data))
        .catch(e => {
          if (e.response && hasIn(e.response, "data")) return e.response.data;
          return {
            success: false,
            data: { message: "Network Error - Please try again" }
          };
        });
    },
    submitRegistration({ state, commit }) {
      const first_name =
        !!state.registration.first_name && !!state.registration.last_name
          ? state.registration.first_name
          : state.registration.business_name;

      const business_name =
        state.registration.business_name &&
        state.registration.business_name.length
          ? state.registration.business_name
          : `${state.registration.first_name} ${state.registration.last_name}`;

      return $http
        .post("/users/register", {
          username: state.registerUsername,
          email: state.registerEmail,
          password: state.registerPassword,
          lab_id: state.registration.lab_id,
          first_name: first_name,
          last_name: state.registration.last_name,
          clients: [
            {
              title: business_name,
              first_name: state.registration.first_name,
              last_name: state.registration.last_name
            }
          ]
        })
        .then(res => {
          const { data } = res;
          commit("SET_ERROR_REGISTRATION", false); // Clear any errors
          if (data.success !== true) {
            commit(
              "SET_ERROR_REGISTRATION",
              data.message ? data.message : undefined
            );
          }
          return data.success === true;
        })
        .catch(e => {
          if (e.request && e.request.status) {
            if (e.request.status === 422) {
              // Check for error response data
              if (hasIn(e.response, "data.data.errors")) {
                // Map errors to a variable
                const { errors } = e.response.data.data;
                if (!isEmpty(errors)) {
                  // Prepare an array to hold duplicate messages
                  const duplicateMessages = [];

                  // Check for duplicate title
                  if (hasIn(errors, "title") && !!errors.title._isUnique) {
                    duplicateMessages.push(errors.title._isUnique);
                  }

                  // Check for duplicate username
                  if (
                    hasIn(errors, "username") &&
                    !!errors.username._isUnique
                  ) {
                    duplicateMessages.push(errors.username._isUnique);
                  }

                  // If any duplicate errors found, combine and return
                  if (duplicateMessages.length > 0) {
                    const message =
                      duplicateMessages.join("<br />") +
                      "<br />Please contact CVAS Admin Support for help";
                    commit("SET_ERROR_REGISTRATION", message);
                    return false;
                  }
                }
              }
            }
          }
          commit("SET_ERROR_REGISTRATION");
          return false;
        });
    },
    copyToFetch({ commit }, token) {
      commit("SET_ERROR_COPY_TO", false);
      return $http
        .get(`/copy-to/${token}`, {
          params: { registration: 1 }
        })
        .then(res => {
          const { data } = res;
          if (!data.success || !data.data) {
            commit("SET_ERROR_COPY_TO");
            return false;
          }
          commit("SET_USER_DATA_COPY_TO", data.data);
          return true;
        })
        .catch(e => {
          console.error(e);
          commit("SET_ERROR_COPY_TO");
          return false;
        });
    },
    copyToRegister({ state, commit, dispatch }, token) {
      const payload = {
        first_name: state.copyToRegistration.data?.first_name?.trim(),
        last_name: state.copyToRegistration.data?.last_name?.trim(),
        business_name: state.copyToRegistration.data?.business_name?.trim(),
        fax: state.copyToRegistration.data?.fax?.trim(),
        o_user: {
          email: state.copyToRegistration.data?.email?.trim(),
          first_name: state.copyToRegistration.data?.first_name?.trim(),
          last_name: state.copyToRegistration.data?.last_name?.trim(),
          username: state.copyToRegistration.data?.username?.trim(),
          password: state.copyToRegistration?.data?.password,
          is_copy_to: true,
          active: true
        }
      };
      return $http
        .put(`/copy-to/${token}`, payload)
        .then(res => {
          const { data } = res;
          if (!data.success || !data.data) {
            commit(
              "SET_ERROR_COPY_TO",
              "There was an issue with your account registration."
            );
            return false;
          }
          return dispatch("login");
        })
        .catch(e => {
          console.error(e);
          commit(
            "SET_ERROR_COPY_TO",
            "There was an issue with your account registration."
          );
          return false;
        });
    }
  }
};
